import { CredentialsCurrentUser, CredentialsService } from '@app/core';
import { ROLES_GROUPS } from '@app/constants';
import { firstValueFrom, take } from 'rxjs';
import { UserClientOptionService } from './user-client-option.service';

export interface PremiumsCatalogLoadContext {
    scopeKey: string;
    clientId?: string;
    applyRestrictedFilter: boolean;
    /** true cuando hay usuario en sesión (Authorization debe enviarse). */
    isAuthenticated: boolean;
}

export interface PremiumsGetProductsRequest {
    categoryId: string;
    clientId?: string | null;
    useAuth: boolean;
}

export interface ResolvePremiumsGetProductsOptions {
    /** Pedidos/admin: fuerza clientId sobre el contexto de sesión MKT. */
    clientIdOverride?: string | null;
    useAuthOverride?: boolean;
}

/** Usuario de sesión para APIs de catálogo (no depende de credentials.tokens en localStorage). */
export function resolvePremiumsSessionUser(
    credentialsService: CredentialsService
): CredentialsCurrentUser | null {
    return credentialsService.credentialsCurrentUser ?? null;
}

export async function ensurePremiumsClientContextLoaded(
    user: CredentialsCurrentUser,
    userClientOptionService: UserClientOptionService
): Promise<void> {
    if (!user?.clientId) {
        return;
    }

    const clientOption = await firstValueFrom(
        userClientOptionService.clientOption$.pipe(take(1))
    );
    if (clientOption) {
        return;
    }

    await firstValueFrom(
        userClientOptionService.getClientAndClientOptions().pipe(take(1))
    );
}

/**
 * Resuelve si las APIs de catálogo premium deben recibir clientId.
 * Proveedor / Proveedor SD: nunca. MKT con catálogo restringido (no exento): sí.
 */
export async function resolvePremiumsCatalogLoadContext(
    user: CredentialsCurrentUser | null,
    userClientOptionService: UserClientOptionService
): Promise<PremiumsCatalogLoadContext> {
    if (!user) {
        return {
            scopeKey: 'guest',
            applyRestrictedFilter: false,
            isAuthenticated: false
        };
    }

    if (ROLES_GROUPS.PROVIDER_GROUP.includes(user.role)) {
        return {
            scopeKey: `provider:${user.id}`,
            applyRestrictedFilter: false,
            isAuthenticated: true
        };
    }

    if (!user.clientId || !ROLES_GROUPS.MKT_GROUP.includes(user.role)) {
        return {
            scopeKey: `auth:${user.id}`,
            applyRestrictedFilter: false,
            isAuthenticated: true
        };
    }

    await ensurePremiumsClientContextLoaded(user, userClientOptionService);

    const [client, clientOption] = await Promise.all([
        firstValueFrom(userClientOptionService.client$.pipe(take(1))),
        firstValueFrom(userClientOptionService.clientOption$.pipe(take(1)))
    ]);

    const clientId = client?.id ?? user.clientId;
    if (!clientOption?.restrictedCatalogue) {
        return {
            scopeKey: `client:${clientId}:open:${user.id}`,
            applyRestrictedFilter: false,
            isAuthenticated: true
        };
    }

    const userEmail = user.email?.toLowerCase();
    const isExempt =
        !!userEmail &&
        (client?.nonRestrictedUsers || []).some(
            email => email?.toLowerCase() === userEmail
        );

    if (isExempt) {
        return {
            scopeKey: `client:${clientId}:exempt:${user.id}`,
            applyRestrictedFilter: false,
            isAuthenticated: true
        };
    }

    return {
        scopeKey: `client:${clientId}:restricted:${user.id}`,
        clientId,
        applyRestrictedFilter: true,
        isAuthenticated: true
    };
}

/**
 * Parámetros unificados para GET premiums-get-products según rol/contexto:
 * - Invitado: useAuth=false, showPublicPrices vía seller service
 * - SD admin/operativo/proveedor SD: useAuth=true, sin clientId
 * - MKT catálogo restringido: useAuth=true, clientId del cliente
 * - Pedido premium: clientIdOverride del pedido
 */
export async function resolvePremiumsGetProductsRequest(
    categoryId: string,
    user: CredentialsCurrentUser | null,
    userClientOptionService: UserClientOptionService,
    options: ResolvePremiumsGetProductsOptions = {}
): Promise<PremiumsGetProductsRequest> {
    const loadContext = await resolvePremiumsCatalogLoadContext(
        user,
        userClientOptionService
    );

    const useAuth = options.useAuthOverride ?? loadContext.isAuthenticated;

    let clientId: string | null = null;
    if (
        options.clientIdOverride !== undefined &&
        options.clientIdOverride !== null
    ) {
        clientId = options.clientIdOverride;
    } else if (loadContext.clientId) {
        clientId = loadContext.clientId;
    }

    return {
        categoryId,
        clientId,
        useAuth
    };
}
